import type { GenerationPreview } from '@/domain/generate';
import type { SyncPlan } from '@/domain/sync';
import type { ApiError } from '@/shared/api';
import type { Workspace } from '@/shared/types';

/**
 * Typed fetch wrapper.
 *
 * Errors carry the server's `code` so the UI can react to the few cases that
 * need a different screen (signed out, no workspace yet) rather than showing a
 * generic failure for everything.
 */

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: ApiError['code'],
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.code === 'unauthorized';
  }

  get isMissingWorkspace(): boolean {
    return this.code === 'no_workspace';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
    // Same-origin only; the session cookie must ride along.
    credentials: 'same-origin',
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : undefined;

  if (!response.ok) {
    const error = (payload ?? {}) as ApiError;
    throw new ApiRequestError(
      error.error ?? `Request failed (${response.status})`,
      response.status,
      error.code,
      error.detail,
    );
  }

  return payload as T;
}

export interface SessionInfo {
  email: string;
  name: string;
  picture?: string;
  scopes: string[];
  hasWorkspace: boolean;
  spreadsheetId: string | null;
}

export interface SyncReportView {
  from: string;
  to: string;
  created: number;
  retitled: number;
  replaced: number;
  deleted: number;
  unchanged: number;
  warnings: string[];
  errors: string[];
  changedAssignments: number;
}

export const api = {
  health: () => request<{ ok: boolean; missing: string[] }>('/api/health'),

  me: () => request<SessionInfo>('/api/me'),

  workspace: () => request<Workspace>('/api/workspace'),

  bootstrap: (body: { riderName: string; timezone?: string; title?: string }) =>
    request<Workspace>('/api/workspace', { method: 'POST', body: JSON.stringify(body) }),

  saveVolunteer: (body: unknown) =>
    request<Workspace>('/api/volunteers', { method: 'POST', body: JSON.stringify(body) }),

  suggestedSeed: (excludeId?: string) =>
    request<{ seedCredit: number; asOf: string }>(
      `/api/volunteers/suggested-seed${excludeId ? `?exclude=${encodeURIComponent(excludeId)}` : ''}`,
    ),

  saveRider: (body: unknown) =>
    request<Workspace>('/api/riders', { method: 'POST', body: JSON.stringify(body) }),

  patchAssignments: (patches: unknown[]) =>
    request<Workspace>('/api/assignments', {
      method: 'POST',
      body: JSON.stringify({ patches }),
    }),

  saveSettings: (body: unknown) =>
    request<Workspace>('/api/settings', { method: 'POST', body: JSON.stringify(body) }),

  previewGeneration: (body: unknown) =>
    request<{ preview: GenerationPreview; revision: string }>('/api/generate/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  applyGeneration: (body: unknown) =>
    request<{ workspace: Workspace; applied: number }>('/api/generate/apply', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  setupCalendars: () =>
    request<{
      workspace: Workspace;
      created: boolean;
      publishedPublicly: boolean;
      note?: string;
    }>('/api/calendar/setup', { method: 'POST', body: '{}' }),

  syncCalendars: (dryRun = false) =>
    request<{ plan: SyncPlan; report: SyncReportView; workspace: Workspace }>(
      '/api/calendar/sync',
      { method: 'POST', body: JSON.stringify({ dryRun }) },
    ),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
};
